import { css, keyframes } from "@emotion/react";
import { Card, Button, Grid, Box, Typography } from "@mui/material";
import { useCustomMediaQuery } from "@unit/hooks";
import { appColor, appTypo, CustomMuiButton, FontFamily, FontWeight } from "@unit/styles";
import { SVGStoreImgInitialTitle } from "@unit/svgs";
import { MultiFactorResolver } from "firebase/auth";
import { useAtom } from "jotai";
import * as localforage from "localforage";
import { useRouter } from "next/router";
import React, { useEffect, useState } from "react";
import { RiLoader2Line } from "react-icons/ri";


import PrimaryForm from "@/components/common/PrimaryForm";
import { useAppSnackbar } from "@/custom-hooks/use-app-snackbar";
import { useAuthentication } from "@/custom-hooks/use-authentication";
import { elevationApp } from "@/global-state/app-config";
import { authUserAtom, idTokenAtom, loadingAtom } from "@/global-state/jotai-atom";
import { homePath } from "@/global-state/store-app-path";

import OTPInput from "../common/input/OtpInput";
import SecurityWarning from "../common/multi-factor/SecurityWarning";

const OTP_LENGTH = 6;

const LoginContainer: React.FC = () => {
  const router = useRouter();

  const { authLoginWithEmail, verifyUserMFA } = useAuthentication();
  const { setAppSnackbar } = useAppSnackbar();
  const { isMobile } = useCustomMediaQuery();

  const [loading] = useAtom(loadingAtom);
  const [idToken] = useAtom(idTokenAtom);
  const [authUser] = useAtom(authUserAtom);

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [otp, setOtp] = useState<string>("");
  const [otpData, setOtpData] = useState<{ resolver: MultiFactorResolver; verificationId: string }>();
  const [isVerifyError, setIsVerifyError] = useState<boolean>(false);

  useEffect(() => {
    localforage.removeItem("mfa_session_flag");
  }, []);

  useEffect(() => {
    (async () => {
      if (authUser?.id && idToken && router.pathname === "/") {
        await router.push(homePath);
      }
    })();
  }, [router.pathname, authUser, idToken]);

  const onClickEmailAuth = async () => {
    if (!email) {
      setAppSnackbar("メールアドレスが未入力です", { error: true });
    } else if (!password) {
      setAppSnackbar("パスワードが未入力です", { error: true });
    } else {
      await authLoginWithEmail(email, password, (value) => setOtpData(value));
    }
  };

  const onClickVerifyAuth = async () => {
    if (!otpData) {
      setAppSnackbar("エラーが発生しました", { error: true });
      return;
    }
    const result = await verifyUserMFA(otpData?.resolver, otpData?.verificationId, otp, true);

    if (!result) {
      setIsVerifyError(true);
    }
  };

  return (
    <Grid px={2}>
      <div id="recaptcha-container" />

      <Card elevation={elevationApp} css={style.container}>
        <Box py={3} px={isMobile ? 3 : 5}>
          {!otpData ? (
            <>
              <Grid container>
                <Grid item xs={12} justifyContent={"center"} textAlign={"center"} pb={2}>
                  <SVGStoreImgInitialTitle />
                </Grid>
                <Grid item xs={12} css={style.title} pb={3}>
                  ユニットカスタマーダッシュボード
                </Grid>
                <Grid item xs={12} pb={2}>
                  <PrimaryForm
                    type={"email"}
                    required={true}
                    label={"unit-admin@store.jp"}
                    title={"メールアドレス"}
                    value={email}
                    error={false}
                    onChange={setEmail}
                  />
                </Grid>
                <Grid item xs={12} pb={2}>
                  <PrimaryForm
                    type={"password"}
                    required={true}
                    title={"パスワード"}
                    value={password}
                    error={false}
                    onChange={setPassword}
                  />
                </Grid>
              </Grid>
              <Grid container justifyContent="center" alignItems="center" pt={3} pb={2}>
                <Button
                  css={CustomMuiButton}
                  type="submit"
                  fullWidth
                  variant="contained"
                  size={"large"}
                  onClick={onClickEmailAuth}
                >
                  ログイン
                </Button>
              </Grid>
            </>
          ) : (
            <>
              <Grid container>
                <Grid item xs={12} justifyContent={"center"} textAlign={"center"} pb={2}>
                  <SVGStoreImgInitialTitle />
                </Grid>
                <Grid item xs={12} css={style.title} pb={3}>
                  ユニットカスタマーダッシュボード
                </Grid>
                <Grid item xs={12} css={style.description} pb={1}>
                  登録されている電話番号へ認証コードを記したSMSをお送りしました。
                </Grid>

                <OTPInput
                  length={OTP_LENGTH}
                  value={otp}
                  onChange={(value) => {
                    setOtp(value);
                    setIsVerifyError(false);
                  }}
                />

                <Grid item xs={12} pt={2}>
                  {isVerifyError ? (
                    <SecurityWarning />
                  ) : (
                    <Typography mt={2} css={style.note}>
                      {`30秒経っても認証コードが届かない方は、\nはじめからやり直してください。`}
                    </Typography>
                  )}
                </Grid>
              </Grid>

              <Grid container justifyContent="center" alignItems="center" gap={1} pt={2}>
                <Button
                  fullWidth
                  variant="contained"
                  size="large"
                  onClick={onClickVerifyAuth}
                  disabled={loading || isVerifyError || otp.length !== OTP_LENGTH}
                  css={[CustomMuiButton, style.button]}
                >
                  {loading ? <RiLoader2Line size={22} css={style.loaderIcon} /> : " 認証"}
                </Button>
                <Button
                  fullWidth
                  variant="outlined"
                  size="large"
                  onClick={() => {
                    setOtp("");
                    setOtpData(undefined);
                  }}
                  css={[CustomMuiButton, style.buttonBorder]}
                >
                  はじめからやり直す
                </Button>
              </Grid>
            </>
          )}
        </Box>
      </Card>
    </Grid>
  );
};
export default LoginContainer;

const spin = keyframes`
  from { transform: rotate(0deg); }
  to   { transform: rotate(360deg); }
`;

const style = {
  container: css`
    max-width: 472px;
    margin: auto;
    border-radius: 16px;
  `,
  title: css`
    font-family: ${appTypo.font.notoJp};
    font-style: normal;
    font-weight: bold;
    font-size: 16px;
    line-height: 24px;
    letter-spacing: 0.1px;
    color: ${appColor.Text.primary};
    text-align: center;
  `,
  description: css`
    font-family: ${FontFamily.kintoSans};
    font-weight: ${FontWeight.medium};
    color: #081735;
    font-size: 16px;
    line-height: 1.4;
  `,
  note: css`
    font-family: ${FontFamily.kintoSans};
    font-weight: ${FontWeight.medium};
    color: #081735;
    font-size: 16px;
    line-height: 1.4;
    white-space: pre-line;
    text-align: center;
  `,
  button: css`
    height: 36px;
  `,
  buttonBorder: css`
    height: 36px;
    width: 100%;
    border: 1px solid #d0d7de;
    color: #656d76;
    background-color: white;
    &:hover {
      background-color: #d0d7de;
      opacity: 1;
    }
    &.Mui-disabled {
      opacity: 0.4;
      color: #656d76;
      background-color: white;
    }
  `,
  loaderIcon: css`
    animation: ${spin} 1.25s linear infinite;
    margin-right: 8px;
    vertical-align: middle;
    font-size: 20px;
  `,
};
